import { ChannelSelector } from '@components/chat/ChannelSelector';
import { useChatStore, ChatChannel, useAppStore } from '@stores';
import { ChatTutorial } from '@components/tutorial/ChatTutorial';
import { useSendMessageMutation, useChatQuery } from '@http';
import { Message } from '@components/chat/ChatMessage';
import { TextInput } from '@components/ui/TextInput';
import { combineMessages } from '@helpers/chat';
import { Button } from '@components/ui/Button';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { useForm } from '@mantine/form';
import * as Shared from '@shared/lib';

const MAX_LENGTH = 180;

export function Chat() {
  const messageContainer = useRef<HTMLDivElement>(null);

  const form = useForm({
    initialValues: {
      message: '',
    },
  });

  const { t } = useTranslation(undefined, { keyPrefix: 'Chat' });
  const { mutateAsync } = useSendMessageMutation();
  const { data, refetch } = useChatQuery();
  const { id } = useAppStore();
  const {
    activeChannelId,
    unreadChannels,
    lobbyMessages,
    messages,
    channels,
    setMessages,
    setChannels,
    removeUnreadChannel,
  } = useChatStore();

  useEffect(() => {
    if (!data) return;

    setChannels(data ?? []);
    setMessages(data.flatMap((channel: ChatChannel) => channel.messages));

    unreadChannels.forEach(channelId => {
      const channelExists = data.some((c: any) => c.id === channelId);
      if (!channelExists) removeUnreadChannel(channelId);
    });
  }, [data]);

  const send = async () => {
    const content = form.values.message.trim();
    const channelId = activeChannelId;
    if (!content) return;

    mutateAsync({ channelId, content });
    form.reset();
  };

  const channelName = () => {
    const channel = channels.find(c => c.id === activeChannelId);
    const type = channel?.type ?? 'LOBBY';
    const channelName = type === 'DIRECT'
      ? channel?.participants.find(p => p.user.id !== id)?.user.username ?? 'DIRECT'
      : t(`ChannelType.${type}`);
    return `[${channelName}]`;
  };

  const scrollToBottom = (force = false) => {
    const container = messageContainer.current;
    if (!container) return;

    const sH = container.scrollHeight;
    const sT = container.scrollTop;
    const cH = container.clientHeight;
    const isAtBottom = Math.abs(sH - sT - cH) < 150;
    if (!isAtBottom && !force) return;

    container.scrollTop = 100000000;
  };

  useEffect(() => {
    scrollToBottom(true);
    removeUnreadChannel(activeChannelId);
  }, [activeChannelId]);

  const onChatMessage = (data: Shared.GlobalChatMessage) => {
    const { channels } = useChatStore.getState();

    if (data.channelId) {
      const channelExists = channels.some(c => c.id === data.channelId);
      if (!channelExists) refetch();
    }

    setTimeout(() => { scrollToBottom(); }, 100);
  };

  useEffect(() => {
    Shared.MessageRegistry.emitter.on(Shared.GlobalChatMessage.Opcode, onChatMessage);
    return () => {
      Shared.MessageRegistry.emitter.off(Shared.GlobalChatMessage.Opcode, onChatMessage);
    };
  }, []);

  const channelMessages = activeChannelId
    ? messages.filter(m => m.channelId === activeChannelId) || []
    : lobbyMessages;
  const combinedMessages = combineMessages(channelMessages
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()));

  const canSend = !!form.values.message.trim() && form.values.message.length <= MAX_LENGTH;

  return (
    <div className="flex h-full">
      <ChannelSelector channels={channels} />
      <ChatTutorial />

      <div className="flex-1 flex flex-col relative">
        <div className="flex-1 overflow-auto relative pt-4 pb-2 bg-gray-950" ref={messageContainer}>
          {channelMessages.length === 0 && (
            <p className="text-gray-400 text-center mt-12 text-sm">
              {t('noMessages')}
            </p>
          )}
          {combinedMessages.map((message, i) => (
            <Message message={message} key={i} />
          ))}
        </div>

        <div className="chat-container-shadow" />

        <form onSubmit={form.onSubmit(send)} className="flex">
          <div className="relative w-full">
            <TextInput
              placeholder={t('placeholder', { channel: channelName() })}
              innerClassName="w-full border-t border-secondary h-12"
              className="w-full"
              {...form.getInputProps('message')}
            />

            <div className="absolute bottom-0.25 right-1 text-[10px] text-gray-500">
              <p className={form.values.message.length > MAX_LENGTH ? 'text-red-500' : ''}>
                {form.values.message.length}/{MAX_LENGTH}
              </p>
            </div>
          </div>
          <Button
            className={`${canSend && 'chat-btn-active'} border-b-0 border-r-0 h-12 w-20`}
            disabled={!canSend}
            glitch={canSend}
          >
            {t('send')}
          </Button>
        </form>
      </div>
    </div>
  );
}